import React, { useState, useMemo } from 'react';
import { FiX, FiZap, FiChevronDown } from 'react-icons/fi';
import CellPicker from './course/CellPicker';
import HoleEntryKeypad from './HoleEntryKeypad';
import { analyseSI, autoFixSI, isBlank } from '../utils/strokeIndex';
import './CourseStatsEditModal.css';

// The two portalled pickers sit above whichever `.modal-overlay` z-index won
// the cascade (3000 / 4000 / 100000 across the page stylesheets that all define
// that class), so they are never opened underneath the modal that launched them.
const PICKER_Z = 100001;

// Edit the scoring-relevant course data (Course Rating, Slope, per-hole Par and
// Stroke Index) and the tournament allowance for a single round. Designed to be
// quickly reachable from the Setup tab so an admin can correct wrong data
// before or in-round — the commonest correction of all being a card whose SIs
// don't match the one hanging in the pro shop.
// `tee`: the specific tee being corrected, when the round is played off more
// than one. CR, Slope, Par and SI all belong to a tee, so with a mixed field the
// question "edit the course data" has no single answer — the caller picks a tee
// row and this edits exactly that tee. With one tee in play `tee` is omitted and
// the figures come from the round's own effective course data, unchanged.
const CourseStatsEditModal = ({
  isOpen,
  onClose,
  onSave,
  course,
  tee,
  allowance,
  canEditCourse,
  canEditAllowance,
  roundStarted,
  // Opened from the "Check the card" prompt: the per-hole pars and stroke
  // indexes are what it asks about, so they open expanded.
  initialShowHoles = false,
}) => {
  // The tee's own card when editing a tee; the round's effective one otherwise.
  const source = tee || course;

  const initialPars = useMemo(() => {
    const pars = source?.hole_pars || [];
    return Array.from({ length: 18 }, (_, i) => (pars[i] != null ? String(pars[i]) : ''));
  }, [source]);

  const initialSis = useMemo(() => {
    const sis = source?.hole_stroke_indices || [];
    return Array.from({ length: 18 }, (_, i) => (sis[i] != null ? String(sis[i]) : ''));
  }, [source]);

  const [cr, setCr] = useState(source?.course_rating != null ? String(source.course_rating) : '');
  const [slope, setSlope] = useState(source?.slope_rating != null ? String(source.slope_rating) : '');
  const [pars, setPars] = useState(initialPars);
  const [sis, setSis] = useState(initialSis);
  const [allowancePct, setAllowancePct] = useState(allowance != null ? String(allowance) : '100');
  const [showHoles, setShowHoles] = useState(initialShowHoles);
  // { field: 'par' | 'si', holeIdx } — the one cell the picker is editing.
  const [editingCell, setEditingCell] = useState(null);
  const [showKeypad, setShowKeypad] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const si = useMemo(() => analyseSI(sis), [sis]);

  if (!isOpen) return null;

  const parTotal = pars.reduce((acc, p) => acc + (parseInt(p, 10) || 0), 0);
  const outTotal = pars.slice(0, 9).reduce((acc, p) => acc + (parseInt(p, 10) || 0), 0);
  const inTotal = pars.slice(9).reduce((acc, p) => acc + (parseInt(p, 10) || 0), 0);

  const parsChanged = pars.some((p, i) => (parseInt(p, 10) || 0) !== (source?.hole_pars?.[i] || 0));
  const sisChanged = sis.some((s, i) => (parseInt(s, 10) || 0) !== (source?.hole_stroke_indices?.[i] || 0));

  // Values go in through the pickers, so they are already 3–5 / 1–18 — there is
  // no typed input on this card to sanitise.
  const setHole = (field, idx, value) => {
    const v = isBlank(value) ? '' : String(value);
    if (field === 'par') setPars(prev => prev.map((p, i) => (i === idx ? v : p)));
    else setSis(prev => prev.map((s, i) => (i === idx ? v : s)));
  };

  const handleSave = async () => {
    setError('');

    // Validate
    const crNum = cr === '' ? null : parseFloat(cr);
    const slopeNum = slope === '' ? null : parseInt(slope, 10);
    const allowNum = allowancePct === '' ? null : parseInt(allowancePct, 10);

    if (canEditCourse) {
      if (crNum != null && (Number.isNaN(crNum) || crNum < 50 || crNum > 90)) {
        setError('Course Rating looks off — expected roughly 50–90.');
        return;
      }
      if (slopeNum != null && (Number.isNaN(slopeNum) || slopeNum < 55 || slopeNum > 155)) {
        setError('Slope must be between 55 and 155.');
        return;
      }
      // A half-corrected SI card gives a shot twice and withholds it elsewhere,
      // so a card being CHANGED has to come out a valid 1–18. A card left alone
      // is not this edit's business — an already-broken one must not block a
      // Course Rating fix.
      if (sisChanged && !si.empty && !si.complete) {
        setError(
          si.duplicates.size > 0
            ? `Stroke index ${[...si.duplicates].sort((a, b) => a - b).join(', ')} is used more than once — each of 1–18 goes on exactly one hole.`
            : `Stroke index incomplete — missing ${si.missing.join(', ')}.`
        );
        setShowHoles(true);
        return;
      }
    }
    if (canEditAllowance && allowNum != null && (Number.isNaN(allowNum) || allowNum < 1 || allowNum > 100)) {
      setError('Allowance must be between 1% and 100%.');
      return;
    }

    // Build only the changes we're allowed to make.
    const courseChanges = {};
    if (canEditCourse) {
      if (crNum !== (source?.course_rating ?? null)) courseChanges.course_rating = crNum;
      if (slopeNum !== (source?.slope_rating ?? null)) courseChanges.slope_rating = slopeNum;
      if (parsChanged) {
        courseChanges.hole_pars = pars.map(p => parseInt(p, 10) || 0);
      }
      if (sisChanged) {
        courseChanges.hole_stroke_indices = sis.map(s => parseInt(s, 10) || 0);
      }
    }

    const allowanceChanged = canEditAllowance && allowNum !== (allowance ?? null);

    if (Object.keys(courseChanges).length === 0 && !allowanceChanged) {
      onClose();
      return;
    }

    setSaving(true);
    try {
      await onSave({
        courseChanges,
        allowance: allowanceChanged ? allowNum : undefined,
        // CR / Slope / Allowance changes alter playing handicaps; pars/SI only
        // alter scoring. Tell the parent which recompute is needed.
        affectsHandicaps:
          'course_rating' in courseChanges ||
          'slope_rating' in courseChanges ||
          allowanceChanged,
      });
      onClose();
    } catch (e) {
      setError(e.message || 'Failed to save changes.');
    } finally {
      setSaving(false);
    }
  };

  // One hole: its number, its par and its SI, each a tap target. Stacking the
  // two figures keeps the nine five-across on a phone — a scorecard's Par and
  // SI rows side by side would need a horizontal scroller inside a modal.
  // The OUT/IN column doubles as the row legend: the total lands on the par
  // row, the word SI on the SI row. That is what the "top par · bottom SI" key
  // used to say, in a row of its own the card could not afford.
  const subtotalCell = (label, total) => (
    <div className="cse-hole-cell cse-par-subtotal" key={label}>
      <span className="cse-hole-num">{label}</span>
      <strong>{total}</strong>
      <span className="cse-si-tag">SI</span>
    </div>
  );

  const holeCell = (idx) => (
    <div className="cse-hole-cell" key={`h${idx}`}>
      <span className="cse-hole-num">{idx + 1}</span>
      <button
        type="button"
        className={`cse-hole-val cse-hole-val--par${pars[idx] ? '' : ' cse-hole-val--empty'}`}
        onClick={() => setEditingCell({ field: 'par', holeIdx: idx })}
        aria-label={`Hole ${idx + 1} par`}
      >
        {pars[idx] || '–'}
      </button>
      <button
        type="button"
        className={`cse-hole-val cse-hole-val--si${sis[idx] ? '' : ' cse-hole-val--empty'}${si.dupHoles.has(idx) ? ' cse-hole-val--dup' : ''}`}
        onClick={() => setEditingCell({ field: 'si', holeIdx: idx })}
        aria-label={`Hole ${idx + 1} stroke index`}
      >
        {sis[idx] || '–'}
      </button>
    </div>
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content course-stats-edit-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cse-header">
          <div>
            <h2>{tee ? `Edit ${tee.name} Tee` : (canEditCourse ? 'Edit Course Data' : 'Edit Allowance')}</h2>
            <p>{course?.name || 'This round'}</p>
          </div>
          <button className="cse-close" onClick={onClose} aria-label="Close"><FiX size={20} /></button>
        </div>

        <div className="cse-fields">
          {canEditCourse && (
            <>
              <label className="cse-field">
                <span className="cse-label">Course Rating</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={cr}
                  onChange={(e) => setCr(e.target.value.replace(/[^0-9.]/g, ''))}
                  placeholder="e.g. 70.1"
                />
              </label>
              <label className="cse-field">
                <span className="cse-label">Slope</span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={slope}
                  onChange={(e) => setSlope(e.target.value.replace(/[^0-9]/g, '').slice(0, 3))}
                  placeholder="55–155"
                />
              </label>
            </>
          )}

          {canEditAllowance && (
            <label className="cse-field">
              <span className="cse-label">Allowance %</span>
              <input
                type="text"
                inputMode="numeric"
                value={allowancePct}
                onChange={(e) => setAllowancePct(e.target.value.replace(/[^0-9]/g, '').slice(0, 3))}
                placeholder="100"
              />
            </label>
          )}
        </div>

        {canEditCourse && (
          <div className="cse-par-section">
            <button type="button" className="cse-par-toggle" onClick={() => setShowHoles(s => !s)}>
              <span className="cse-label">Par &amp; Stroke Index</span>
              <span className="cse-par-total">
                {`Par ${parTotal} · SI ${si.empty ? 'not set' : (si.complete ? '1–18' : 'incomplete')}`}
              </span>
              <FiChevronDown
                className={`cse-par-chevron${showHoles ? ' cse-par-chevron--open' : ''}`}
                size={16}
              />
            </button>

            {showHoles && (
              <div className="cse-par-grid-wrap">
                <div className="cse-par-grid">
                  {Array.from({ length: 9 }, (_, i) => holeCell(i))}
                  {subtotalCell('OUT', outTotal)}
                </div>
                <div className="cse-par-grid">
                  {Array.from({ length: 9 }, (_, i) => holeCell(i + 9))}
                  {subtotalCell('IN', inTotal)}
                </div>

                {/* ONE row under the card, not two either side of it. A valid
                    card needs no verdict line — the section header already
                    reads "SI 1–18" — so this row carries the quick-fill button
                    until something is wrong, and the fix when it is. */}
                {!si.empty && !si.complete ? (
                  <div className="cse-si-warning">
                    <span>
                      {si.duplicates.size > 0
                        ? `SI ${[...si.duplicates].sort((a, b) => a - b).join(', ')} twice. `
                        : ''}
                      {si.missing.length > 0 ? `Missing ${si.missing.join(', ')}.` : ''}
                    </span>
                    <button type="button" className="cse-si-fix" onClick={() => setSis(autoFixSI(sis).map(String))}>
                      Auto-fix
                    </button>
                  </div>
                ) : (
                  <button type="button" className="cse-quickfill" onClick={() => setShowKeypad(true)}>
                    <FiZap size={13} /> Run through all 18
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {roundStarted && (
          <p className="cse-note">
            Round underway — saving re-scores every card. CR, Slope and Allowance also redo
            each player's playing handicap.
          </p>
        )}

        {error && <p className="cse-error">{error}</p>}

        <div className="cse-actions">
          <button className="cse-btn cse-btn-cancel" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="cse-btn cse-btn-save" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>

        {editingCell && (
          <CellPicker
            type={editingCell.field}
            holeNumber={editingCell.holeIdx + 1}
            value={editingCell.field === 'par' ? pars[editingCell.holeIdx] : sis[editingCell.holeIdx]}
            strokeIndices={sis}
            currentHoleIdx={editingCell.holeIdx}
            overlayZIndex={PICKER_Z}
            onSelect={(v) => { setHole(editingCell.field, editingCell.holeIdx, v); setEditingCell(null); }}
            onClear={() => { setHole(editingCell.field, editingCell.holeIdx, ''); setEditingCell(null); }}
            onClose={() => setEditingCell(null)}
          />
        )}

        {showKeypad && (
          <HoleEntryKeypad
            holePars={pars}
            holeStrokeIndices={sis}
            onUpdate={(field, holeIdx, value) => setHole(field === 'hole_pars' ? 'par' : 'si', holeIdx, value)}
            onClose={() => setShowKeypad(false)}
            overlayZIndex={PICKER_Z}
            allowDuplicateSI
          />
        )}
      </div>
    </div>
  );
};

export default CourseStatsEditModal;
